import { useEffect } from 'react';

type Props = {
  parentRef: React.RefObject<HTMLElement>;
  childRef: React.RefObject<HTMLElement>;
};

export const useNestedScrollControl = ({ parentRef, childRef }: Props) => {
  useEffect(() => {
    const parent = parentRef.current;
    const child = childRef.current;

    if (!parent || !child) return;

    const epsilon = 1; // tolerance for scroll rounding
    let startY = 0; // for touch start tracking

    /**
     * WHEEL SCROLL (Desktop)
     * - Scrolls child first when going down
     * - Scrolls child when going up ONLY if the page is already at the top
     */
    const handleWheel = (e: WheelEvent) => {
      const deltaY = e.deltaY;

      const childAtTop = child.scrollTop <= 0;
      const childAtBottom = child.scrollTop + child.clientHeight >= child.scrollHeight - epsilon;
      const pageAtTop = window.scrollY <= 0;

      if (
        (deltaY > 0 && !childAtBottom) || // Scroll down into child
        (deltaY < 0 && pageAtTop && !childAtTop) // Scroll up into child if page is at top
      ) {
        e.preventDefault();
        // Instant scroll for desktop
        child.scrollTop += deltaY;
      }
    };

    /**
     * TOUCH START
     * - Store the initial finger Y position
     */
    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      startY = touch.clientY;
    };

    /**
     * TOUCH MOVE (Mobile)
     * - Calculates scroll delta
     * - Scrolls child smoothly at slower speed
     * - Only scrolls child if same conditions as wheel
     */
    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches?.[0];
      if (!touch) return;

      const currentY = touch.clientY;
      const deltaY = startY - currentY; // positive = swipe up, negative = swipe down
      const scrollSpeed = 0.5; // slow down touch scrolling for smoother feel

      const childAtTop = child.scrollTop <= 0;
      const childAtBottom = child.scrollTop + child.clientHeight >= child.scrollHeight - epsilon;
      const pageAtTop = window.scrollY <= 0;

      if (
        (deltaY > 0 && !childAtBottom) || // Swipe up to scroll down into child
        (deltaY < 0 && pageAtTop && !childAtTop) // Swipe down to scroll up into child from top
      ) {
        e.preventDefault();
        child.scrollBy({
          top: deltaY * scrollSpeed,
          behavior: 'smooth',
        });
      }
    };

    parent.addEventListener('wheel', handleWheel, { passive: false });
    parent.addEventListener('touchstart', handleTouchStart, { passive: true });
    parent.addEventListener('touchmove', handleTouchMove, { passive: false });

    // Cleanup on unmount
    return () => {
      parent.removeEventListener('wheel', handleWheel);
      parent.removeEventListener('touchstart', handleTouchStart);
      parent.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);
};
